/**
 * QrPunchButton — scan the Site QR poster to clock IN (when off shift) or
 * clock OUT (when on shift). Same poster, both directions (Aug 2026).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { QrCode, Loader2 } from 'lucide-react';
import QrScanDialog from '../QrScanDialog';
import MealBreakDialog from '../MealBreakDialog';
import { useToast } from '../../hooks/use-toast';
import { enqueuePunch, isNetworkError } from '../../utils/offlineQueue';

const API = `${window.__BACKEND_URL__}/api`;

export const QrPunchButton = ({ token, clockedIn, onDone, className = '' }) => {
  const { toast } = useToast();
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mealPrompt, setMealPrompt] = useState(null); // { qrToken, location, shift_hours, job }
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const resolveMealPrompt = async (deductBreak) => {
    const { qrToken, location, job } = mealPrompt;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/clock-out/quick`,
        { site_qr_token: qrToken, location, deduct_break: deductBreak, confirm_no_break: !deductBreak },
        auth,
      );
      toast({
        title: 'Clocked out',
        description: data?.meal_break_deducted
          ? '30-min unpaid meal break deducted (Alberta ESA). Shift saved.'
          : `Shift ended at ${job?.job_title || 'site'}. Have a good one!`,
      });
      setMealPrompt(null);
      onDone?.('out', job);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: 'Clock out failed', description: (typeof d === 'string' ? d : d?.message) || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const getLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000 },
    );
  });

  const handleScan = async (qrToken) => {
    setScanning(false);
    setBusy(true);
    try {
      let job = null;
      try {
        const { data } = await axios.get(`${API}/clock-in/resolve-qr`, { ...auth, params: { token: qrToken } });
        job = data;
      } catch (err) {
        if (isNetworkError(err)) {
          // No signal — queue the raw token; the server resolves it on sync.
          const location = await getLocation();
          enqueuePunch(clockedIn ? 'clock_out' : 'clock_in', { site_qr_token: qrToken, location });
          toast({
            title: clockedIn ? 'Clock-out saved offline' : 'Clock-in saved offline',
            description: 'It will sync when you get signal.',
          });
          onDone?.(clockedIn ? 'out' : 'in', null);
          return;
        }
        throw err;
      }
      const location = await getLocation();
      if (clockedIn) {
        try {
          const { data } = await axios.post(`${API}/clock-out/quick`, { site_qr_token: qrToken, location }, auth);
          if (data?.meal_break_warning) {
            // Alberta ESA — server holds the clock-out until the worker answers.
            setMealPrompt({ qrToken, location, shift_hours: data.shift_hours, job });
            return;
          }
          toast({ title: 'Clocked out', description: `Shift ended at ${job.job_title || 'site'}. Have a good one!` });
        } catch (err) {
          if (!isNetworkError(err)) throw err;
          enqueuePunch('clock_out', { site_qr_token: qrToken, location });
          toast({ title: 'Clock-out saved offline', description: 'It will sync when you get signal.' });
        }
        onDone?.('out', job);
      } else {
        try {
          await axios.post(`${API}/clock-in/quick`, { job_id: job.job_id, location, site_qr_token: qrToken }, auth);
          toast({ title: 'Clocked in', description: `${job.job_number ? `${job.job_number} — ` : ''}${job.job_title || 'Site'}` });
        } catch (err) {
          if (!isNetworkError(err)) throw err;
          enqueuePunch('clock_in', { job_id: job.job_id, location, site_qr_token: qrToken });
          toast({ title: 'Clock-in saved offline', description: 'It will sync when you get signal.' });
        }
        onDone?.('in', job);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({
        title: 'QR scan failed',
        description: (typeof d === 'string' ? d : d?.message) || 'Try again or punch manually.',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setScanning(true)}
        disabled={busy}
        className={`w-full rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition active:scale-[0.98] disabled:opacity-50 ${className}`}
        style={{ minHeight: '52px', border: '1px solid rgba(30,136,229,0.45)', background: 'rgba(30,136,229,0.12)', color: '#7DD3FC' }}
        data-testid="qr-punch-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
        {busy ? 'Punching…' : clockedIn ? 'Scan Site QR to Clock Out' : 'Scan Site QR to Clock In'}
      </button>
      <QrScanDialog
        open={scanning}
        title={clockedIn ? 'Scan to Clock Out' : 'Scan to Clock In'}
        description="Point your camera at the QR poster at your job site."
        skipLabel="Close — punch manually instead"
        onScan={handleScan}
        onSkip={() => setScanning(false)}
        onClose={() => setScanning(false)}
      />
      <MealBreakDialog
        open={!!mealPrompt}
        shiftHours={mealPrompt?.shift_hours}
        busy={busy}
        onDeduct={() => resolveMealPrompt(true)}
        onNoBreak={() => resolveMealPrompt(false)}
        onCancel={() => setMealPrompt(null)}
      />
    </>
  );
};

export default QrPunchButton;
